<template>
	<div>
		<div class="calendar-date d-flex justify-space-around">
			<div>
				<span>【工程予定】</span>
				<span> {{ displayStartTime }}〜{{ displayEndTime }}</span>
			</div>
			<div>
				<span>【現在表示している日程】</span>
				<span> {{ displayDate | dateFormatter }}</span>
			</div>
		</div>
		<div class="d-flex justify-space-between my-2">
			<v-btn class="ml-1" color="primary" @click="$emit('back')">戻る</v-btn>
			<div class="mr-1">
				<v-btn color="calendar-btn" @click="jumpToPrevious" :disabled="stepNumber <= min">
					<v-icon>mdi-chevron-triple-left</v-icon>
				</v-btn>
				<v-btn
					color="calendar-btn"
					:disabled="stepNumber <= min"
					@click="previous"
					v-model="stepNumber"
					>前日</v-btn
				>
				<v-btn color="calendar-btn" :disabled="stepNumber >= max" @click="next" v-model="stepNumber"
					>翌日</v-btn
				>
				<v-btn color="calendar-btn" :disabled="stepNumber >= max" @click="jumpToNext">
					<v-icon>mdi-chevron-triple-right</v-icon>
				</v-btn>
			</div>
		</div>

		<div class="gantt-chart-area">
			<div class="gantt-day">
				<div v-for="(time, index) in times" :key="index">
					<div
						class="gantt-days"
						:style="`width:${block_size}px;left:${time.block_number * block_size}px`"
					>
						<span>{{ time.time }}</span>
					</div>
				</div>
			</div>
			<div class="gantt-height">
				<div v-for="(time, index) in times" :key="index">
					<div
						class="days-height"
						:style="`width:${block_size}px;left:${
							time.block_number * block_size
						}px;height:${calendarViewHeight}px`"
					></div>
				</div>
			</div>

			<div
				class="gantt-bar-area"
				:style="`width:${calendarViewWidth}px;height:${calendarViewHeight}px`"
			>
				<div :style="barStyle" class="gantt-bar">
					<div
						ref="gantt_bar_left"
						v-if="changeDatetimeFlag"
						class="gantt-bar-left"
						style="top: 6px; left: -6px; cursor: col-resize"
						@mousedown.stop="mouseDownResize(selectedTask, 'left')"
					>
						<span
							v-show="stepNumber == min && isShowDisplayTime && leftResizing"
							class="display-time-left"
						>
							{{ formatDisplayTime }}
						</span>
					</div>

					<div
						ref="gantt_bar_right"
						v-if="changeDatetimeFlag"
						class="gantt-bar-right"
						style="top: 6px; right: -6px; cursor: col-resize"
						@mousedown.stop="mouseDownResize(selectedTask, 'right')"
					>
						<span
							v-show="stepNumber == max && isShowDisplayTime && rightResizing"
							class="display-time-right"
						>
							{{ formatDisplayTime }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment/min/moment.min.js"
import "moment/locale/ja"

export default {
	filters: {
		dateFormatter: function (date) {
			if (!date) return ""
			return moment(date).format("YYYY年MM月DD日")
		},
		datetimeFormatter: function (datetime) {
			if (!datetime) return ""
			return moment(datetime).format("YYYY年MM月DD日 HH時mm分")
		},
	},
	props: {
		times: {
			type: Array,
			default: () => [],
		},
		startDateKey: {
			type: String,
			default: "",
		},
		endDateKey: {
			type: String,
			default: "",
		},
		startTimeKey: {
			type: String,
			default: "",
		},
		endTimeKey: {
			type: String,
			default: "",
		},
		block_size: {
			type: Number,
		},
		calendarViewHeight: {
			type: Number,
		},
		calendarViewWidth: {
			type: Number,
		},
		changeDatetimeFlag: {
			type: Boolean,
		},
		barStyle: {
			type: Object,
			default: () => {},
		},
		selectedTask: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			diff: null,
			max: null,
			min: 1,
			currentStepNumber: null,
			stepNumber: 1,
			displayDate: null,
			leftResizing: false,
			rightResizing: false,
			pageX: null,
			element: null,
			left: null,
			task_id: null,
			width: null,
			isShowDisplayTime: false,
			displayTime: null,
		}
	},
	mounted() {
		this.setSelectedTask()
		window.addEventListener("mouseup", this.stopDrag)
		window.addEventListener("mousemove", this.mouseResize)
	},
	methods: {
		setSelectedTask() {
			const start_date = moment(this.selectedTask[this.startDateKey]).date()
			const end_date = moment(
				this.selectedTask[this.endDateKey]
					? this.selectedTask[this.endDateKey]
					: this.selectedTask[this.startDateKey]
			).date()
			this.max = parseInt(end_date) - parseInt(start_date) + 1

			this.displayDate = this.selectedTask[this.startDateKey]
			this.makeBarStyle()
		},
		previous() {
			if (this.stepNumber === this.min) return
			this.stepNumber--
			this.displayDate = moment(this.displayDate).add(-1, "day")
			this.makeBarStyle()
		},
		next() {
			if (this.stepNumber === this.max) return
			this.stepNumber++
			this.displayDate = moment(this.displayDate).add(1, "day")
			this.makeBarStyle()
		},
		jumpToNext() {
			if (this.stepNumber > this.max) return
			this.stepNumber = this.max
			this.displayDate = this.selectedTask[this.endDateKey]
				? this.selectedTask[this.endDateKey]
				: this.selectedTask[this.startDateKey]

			this.makeBarStyle()
		},
		jumpToPrevious() {
			if (this.stepNumber < this.min) return
			this.stepNumber = this.min
			this.displayDate = this.selectedTask[this.startDateKey]
			this.makeBarStyle()
		},
		makeBarStyle() {
			const TIME_OF_DAY = 24
			const FIVE_MINUTES_PIXEL = this.block_size / 12
			if (this.stepNumber === this.min && this.stepNumber === this.max) {
				const startTime = moment(this.startTime())
				const hour = startTime.format("HH")
				const minutes = startTime.format("mm")
				const minutes_pixel = (minutes / 5) * FIVE_MINUTES_PIXEL
				const endTime = moment(this.endTime())
				const left = parseInt(hour) * this.block_size + minutes_pixel
				const width = (endTime.diff(startTime, "minutes") / 5) * FIVE_MINUTES_PIXEL
				this.barStyle.width = `${width}px`
				this.barStyle.left = `${left}px`
				this.$refs.gantt_bar_left.style.display = "block"
				this.$refs.gantt_bar_right.style.display = "block"
			} else if (this.stepNumber === this.min) {
				// 開始日時のbar
				const hour = moment(this.startTime()).format("HH")
				const minutes = moment(this.startTime()).format("mm")
				const minutes_pixel = (minutes / 5) * FIVE_MINUTES_PIXEL
				const left = parseInt(hour) * this.block_size + minutes_pixel
				const width = this.block_size * TIME_OF_DAY - left

				this.barStyle.width = `${width}px`
				this.barStyle.left = `${left}px`
				this.$refs.gantt_bar_left.style.display = "block"
				this.$refs.gantt_bar_right.style.display = "none"
			} else if (this.stepNumber === this.max) {
				// 終了日時のbar
				let hour = moment(this.endTime()).format("HH")
				// hour = parseInt(hour) + 1
				const minutes = moment(this.endTime()).format("mm")
				const minutes_pixel = (minutes / 5) * FIVE_MINUTES_PIXEL

				const width = parseInt(hour) * this.block_size + minutes_pixel
				this.barStyle.width = `${width}px`
				this.barStyle.left = `0px`
				this.$refs.gantt_bar_left.style.display = "none"
				this.$refs.gantt_bar_right.style.display = "block"
			} else {
				// 開始日時、終了日時以外のbar
				const width = this.block_size * 24
				this.barStyle.width = `${width}px`
				this.barStyle.left = `0px`
				this.$refs.gantt_bar_left.style.display = "none"
				this.$refs.gantt_bar_right.style.display = "none"
			}
		},
		mouseDownResize(task, direction) {
			this.isShowDisplayTime = true
			direction === "left" ? (this.leftResizing = true) : (this.rightResizing = true)
			this.pageX = event.pageX
			this.width = event.target.parentElement.style.width
			this.left = event.target.parentElement.style.left
			this.element = event.target.parentElement
			this.task_id = task.id
		},
		mouseResize(event) {
			if (this.leftResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) + diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) + diff}px`
					this.element.style.left = `${this.left.replace("px", "") - diff}px`
				}
				this.showDisplayTime(diff)
			}
			if (this.rightResizing) {
				let diff = this.pageX - event.pageX
				if (parseInt(this.width.replace("px", "")) - diff > this.block_size) {
					this.element.style.width = `${parseInt(this.width.replace("px", "")) - diff}px`
				}
				this.showDisplayTime(diff)
			}
		},
		calculateMinutes(diff) {
			this.diff = diff
			const DIGIT = 1
			const INIT_NUM = 0.08
			const MINUTES_INTERVAL = 5
			let times = parseFloat(this.diff / this.block_size).toFixed(DIGIT)
			const minutes = Math.round(times / INIT_NUM) * MINUTES_INTERVAL

			return minutes
		},
		stopDrag() {
			let beginningOfDay = moment(this.displayDate).startOf("day")
			let endOfDay = moment(this.displayDate).endOf("day")
			let diff = this.pageX - event.pageX

			if (this.leftResizing) {
				if (!moment(this.displayTime).isBetween(beginningOfDay, endOfDay, "minute")) {
					this.selectedTask[this.startTimeKey] = beginningOfDay.format("HH:mm")
					this.selectedTask[this.startDateKey] = beginningOfDay.format("YYYY-MM-DD")
					this.$emit("change-datetime", this.selectedTask)
					this.makeBarStyle()
				} else {
					const minutes = this.calculateMinutes(diff)
					let start_date = moment(this.startTime()).add(-minutes, "minutes")
					this.selectedTask[this.startTimeKey] = start_date.format("HH:mm")
					this.selectedTask[this.startDateKey] = start_date.format("YYYY-MM-DD")
					this.$emit("change-datetime", this.selectedTask)
					this.makeBarStyle()
				}
			}
			if (this.rightResizing) {
				if (!moment(this.displayTime).isBetween(beginningOfDay, endOfDay, "minute")) {
					this.selectedTask[this.endDateKey] = endOfDay.format("YYYY-MM-DD")
					this.selectedTask[this.endTimeKey] = endOfDay.format("HH:mm")
					this.$emit("change-datetime", this.selectedTask)
					this.makeBarStyle()
				} else {
					const minutes = this.calculateMinutes(diff)
					let end_date = moment(this.endTime()).add(-minutes, "minutes")
					this.selectedTask[this.endDateKey] = end_date.format("YYYY-MM-DD")
					this.selectedTask[this.endTimeKey] = end_date.format("HH:mm")
					this.$emit("change-datetime", this.selectedTask)
					this.makeBarStyle()
				}
			}
			this.leftResizing = false
			this.rightResizing = false
			this.isShowDisplayTime = false
		},
		showDisplayTime(diff) {
			const minutes = this.calculateMinutes(diff)
			if (this.leftResizing) {
				let times = moment(this.startTime()).add(-minutes, "minutes")
				this.displayTime = times.format("YYYY-MM-DD HH:mm")
			}
			if (this.rightResizing) {
				let times = moment(this.endTime()).add(-minutes, "minutes")
				this.displayTime = times.format("YYYY-MM-DD HH:mm")
			}
		},
		startTime() {
			return this.selectedTask[this.startTimeKey]
				? this.displayDate + "T" + moment(this.selectedTask[this.startTimeKey]).format("HH:mm")
				: `${this.displayDate}T00:00`
		},
		endTime() {
			return this.selectedTask[this.endTimeKey]
				? this.displayDate + "T" + moment(this.selectedTask[this.endTimeKey]).format("HH:mm")
				: `${this.displayDate}T23:55`
		},
	},
	computed: {
		formatDisplayTime() {
			let beginningOfDay = moment(this.displayDate).startOf("day")
			let endOfDay = moment(this.displayDate).endOf("day")
			if (!moment(this.displayTime).isBetween(beginningOfDay, endOfDay, "minute")) {
				return (this.displayTime = 0)
			} else {
				return moment(this.displayTime).format("HH:mm")
			}
		},
		displayStartTime() {
			moment.locale("ja")
			if (!this.selectedTask[this.startDateKey]) {
				return ""
			} else if (this.selectedTask[this.startTimeKey]) {
				return (
					moment(this.selectedTask[this.startDateKey]).format("YYYY年MM月DD日(dd)") +
					moment(this.startTime()).format(" HH:mm")
				)
			} else {
				return moment(this.selectedTask[this.startDateKey]).format("YYYY年MM月DD日(dd) 時刻未設定")
			}
		},
		displayEndTime() {
			if (!this.selectedTask[this.endDateKey]) {
				return ""
			} else if (this.selectedTask[this.endTimeKey]) {
				moment.locale("ja")
				return (
					moment(this.selectedTask[this.endDateKey]).format("YYYY年MM月DD日(dd)") +
					moment(this.endTime()).format(" HH:mm")
				)
			} else {
				moment.locale("ja")
				return moment(this.selectedTask[this.endDateKey]).format("YYYY年MM月DD日(dd) 時刻未設定")
			}
		},
	},
	watch: {
		selectedTask(newVal, _) {
			this.selectedTask = newVal
			this.setSelectedTask()
		},
		deep: true,
	},
}
</script>

<style scoped lang="scss">
@import "../../../stylesheet/shared/color.scss";

.display-time-left {
	background: #7e8893;
	position: absolute;
	padding: 10px;
	border-radius: 5px;
	bottom: 27px;
	color: white;
	width: 70px;
	text-align: center;
	opacity: 0.8;
}

.display-time-right {
	background: #7e8893;
	position: absolute;
	padding: 10px;
	border-radius: 5px;
	bottom: 27px;
	color: white;
	width: 70px;
	text-align: center;
	opacity: 0.8;
	right: 6px;
}

.gantt-chart-area {
	overflow-x: scroll;
}

.gantt-height {
	position: relative;
}
.gantt-day {
	position: relative;
	height: 3rem;
}
.gantt-days {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	height: 3rem;
	font-size: 0.8rem;
	flex-direction: column;
}
.days-height {
	position: absolute;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
}
.gantt-bar-area {
	position: relative;
}

.gantt-bar-left {
	position: absolute;
	background: #808080a1;
	border: 1px solid black;
	height: 8px;
	width: 8px;
}
.gantt-bar-right {
	position: absolute;
	background: #808080a1;
	border: 1px solid black;
	height: 8px;
	width: 8px;
}
.calendar-date {
	background: $main-color;
	border-bottom: solid 1px #80808045;
	border-right: solid 1px #80808045;
	border-top: solid 1px #80808045;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 2rem;
	span {
		color: white;
		font-weight: bold;
	}
}
.gantt-bar {
	position: relative;
	background: orange;
	height: 20px;
	border-radius: 10px;
}

.calendar-btn {
	background-color: #7e8893 !important;
	color: white;
}
</style>
